/**
 * Language Mapper Utility
 * Maps custom API language codes to standard i18n language codes
 */

export type ApiLanguageCode = 'en' | 'sp' | 'fr' | 'gr';
export type I18nLanguageCode = 'en' | 'es' | 'fr' | 'de';

/**
 * Language code mapping configuration
 * Maps API language codes to i18n language codes
 * Supports both short codes (en, sp, fr, gr) and full names (english, spanish, etc.)
 */
const LANGUAGE_CODE_MAP: Record<string, I18nLanguageCode> = {
  // Short codes (from LanguageDropdown)
  en: 'en', // English → English
  sp: 'es', // Spanish (API) → Spanish (i18n)
  fr: 'fr', // French → French
  gr: 'de', // German (API) → German (i18n)
  // Full names (possibly from API response)
  english: 'en',
  spanish: 'es',
  french: 'fr',
  german: 'de',
  // i18n codes (in case they come through)
  es: 'es',
  de: 'de',
};

/**
 * Reverse mapping: i18n codes to API codes
 * Used when sending language preference back to API
 */
const REVERSE_LANGUAGE_MAP: Record<I18nLanguageCode, ApiLanguageCode> = {
  en: 'en',
  es: 'sp',
  fr: 'fr',
  de: 'gr',
};

/**
 * Maps API language code to i18n-compatible language code
 * @param apiLangCode - Language code from API (en, sp, fr, gr)
 * @returns Standard i18n language code (en, es, fr, de)
 */
export const mapApiLangToI18n = (apiLangCode: string): I18nLanguageCode => {
  const normalizedCode = apiLangCode?.toLowerCase() || '';
  const mappedCode = LANGUAGE_CODE_MAP[normalizedCode];

  if (!mappedCode) {
    console.warn(
      `[Language Mapper] Unknown API language code: "${apiLangCode}". Falling back to English.`
    );
    return 'en'; // Default fallback to English
  }

  return mappedCode;
};

/**
 * Maps i18n language code to API-compatible language code
 * @param i18nLangCode - i18n language code (en, es, fr, de)
 * @returns API language code (en, sp, fr, gr)
 */
export const mapI18nLangToApi = (i18nLangCode: I18nLanguageCode): ApiLanguageCode => {
  const mappedCode = REVERSE_LANGUAGE_MAP[i18nLangCode];

  if (!mappedCode) {
    console.warn(
      `[Language Mapper] Unknown i18n language code: "${i18nLangCode}". Falling back to English.`
    );
    return 'en';
  }

  return mappedCode;
};

/**
 * Validates if a language code is supported
 * @param langCode - Language code to validate
 * @returns True if supported, false otherwise
 */
export const isSupportedLanguage = (langCode: string): boolean => {
  return Object.keys(LANGUAGE_CODE_MAP).includes(langCode?.toLowerCase());
};

/**
 * Gets all supported API language codes
 * @returns Array of supported API language codes
 */
export const getSupportedApiLanguages = (): ApiLanguageCode[] => {
  return Object.keys(LANGUAGE_CODE_MAP) as ApiLanguageCode[];
};

/**
 * Gets all supported i18n language codes
 * @returns Array of supported i18n language codes
 */
export const getSupportedI18nLanguages = (): I18nLanguageCode[] => {
  return Object.values(LANGUAGE_CODE_MAP) as I18nLanguageCode[];
};